import React, { useMemo } from 'react'
import { useCartShippingDiscountsQuery } from '@graphql/queries/__generated__/cart.generated'
import _ from 'lodash'
import FormattedPrice from '@components/FormattedPrice'
import { useReactiveVar } from '@apollo/client'
import { isWholesaleModeVar } from '../../../../apolloClient'
import { CartTypeEnum } from '../../../../generated/gqlTypes'
import { useCurrentCurrencyExchangeRateQuery } from '@graphql/queries/__generated__/shipping.generated'
import Divider from '@mui/material/Divider'

const CartShippingDiscount = () => {
  const isWholesaleMode = useReactiveVar(isWholesaleModeVar)
  const { data } = useCartShippingDiscountsQuery({
    variables: { cartType: isWholesaleMode ? CartTypeEnum.Wholesale : CartTypeEnum.Default },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  })

  const currencyExchangeRateQuery = useCurrentCurrencyExchangeRateQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  })

  const [currency, rate] = useMemo(() => {
    if (!currencyExchangeRateQuery.data?.currentCurrencyExchangeRate) return ['USD', 1]
    return [
      currencyExchangeRateQuery.data.currentCurrencyExchangeRate.currencyCode,
      currencyExchangeRateQuery.data.currentCurrencyExchangeRate.rate,
    ]
  }, [currencyExchangeRateQuery.data])

  if (!data) return null

  const shippingDiscounts = data.cart?.shippingDiscounts || []
  const sortedDiscounts = _.sortBy(shippingDiscounts, 'value')
  const needSpent = sortedDiscounts.length
    ? sortedDiscounts[sortedDiscounts.length - 1].minValue - data.cart.totalPrice.discountUsd.gross
    : null
  if (!needSpent) return null

  const milestones = sortedDiscounts.map(discount => ({
    id: discount.id,
    minValue: discount.minValue,
    maxValue: discount.maxValue,
    value: discount.value,
    label:
      discount.value === 100 ? (
        <>
          <span className='font-semibold'>Free Shipping</span> for{' '}
          <FormattedPrice
            currency={currency}
            amount={discount.minValue * rate}
            options={{ maximumFractionDigits: 0 }}
          />{' '}
          order
        </>
      ) : (
        <>
          <span className='font-semibold'>{discount.value}% discount</span> for{' '}
          <FormattedPrice
            currency={currency}
            amount={discount.minValue * rate}
            options={{ maximumFractionDigits: 0 }}
          />{' '}
          order
        </>
      ),
  }))

  const currentAmount = data.cart.totalPrice.discountUsd.gross

  const activeIndex = milestones.reduce((acc, m, index) => (currentAmount >= m.minValue ? index : acc), -1)

  const calculateProgress = (index: number) => {
    if (index <= activeIndex) return 100
    if (index > activeIndex + 1) return 0

    if (activeIndex === milestones.length - 1) {
      return 100
    } else if (activeIndex === -1) {
      const next = milestones[activeIndex + 1]
      return Math.min((currentAmount * 100) / next.minValue, 90)
    } else if (activeIndex >= 0) {
      const prev = milestones[activeIndex]
      const next = milestones[activeIndex + 1]
      const progress = ((currentAmount - prev.minValue) * 100) / (next.minValue - prev.minValue)
      return Math.min(progress, 90)
    }
    return 0
  }

  return (
    <div className='fsp-vertical'>
      <div className='fsp-card'>
        <div className='fsp-text'>
          {needSpent > 0 ? (
            <>
              You're only&nbsp;
              <span className='font-semibold'>
                <FormattedPrice currency={currency} amount={needSpent * rate} />
              </span>
              &nbsp;away from Free Shipping
            </>
          ) : (
            'You unlocked Free Shipping'
          )}
        </div>
        <Divider />
        <div className='fsp-tracks'>
          {milestones.map((m, index) => {
            const active = currentAmount >= m.minValue

            return (
              <div key={m.id} className='fsp-track'>
                <div className='fsp-progress' style={{ width: `${calculateProgress(index)}%` }} />
                <div className={`fsp-dot ${active ? 'active' : ''}`} />

                <span className={`fsp-label ${active ? 'active' : ''}`}>{m.label}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default CartShippingDiscount
